import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AddOneCategory from "./AddOneCategory";
import { jwtDecode } from "jwt-decode";
import { Link } from "react-router-dom";
import BulkCategoriesImport from "./BulkCategoriesImport";
import Pagination from "./PaginationComp";
import api from "../utils/api";
import Swal from "sweetalert2";
import { Edit, Trash2, Plus } from "lucide-react";

// ChartJS.register(
//   CategoryScale,
//   LinearScale,
//   PointElement,
//   LineElement,
//   Title,
//   Tooltip,
//   Legend
// );

const Categories = () => {
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [categoryToUpdate, setCategoryToUpdate] = useState(null);

  const [data, setData] = useState([]);

  const [filter, setFilter] = useState("All Transactions");
  const [userId, setUserId] = useState("");

  const [currentPage, setCurrentPage] = useState(1);

    const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decodedToken = jwtDecode(token);
      if (decodedToken.user) {
        setUserId(decodedToken.user.id);
      }
    }
  }, []);

  const fetchData = async (page = currentPage, selectedFilter = filter) => {
    try {
      const response = await api.get(`newcategories/${userId}?page=${page}&limit=10&filter=${selectedFilter}`);

      setData(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch categories. Please try again.");
    }
  };

  useEffect(() => {
    if (userId) {
      fetchData(currentPage, filter);
    }
  }, [userId, currentPage, filter]);

  const handleUpdateClick = (item) => {
    setCategoryToUpdate(item);
    setShowAddCategory(true);
  };

  const handleAddCategory = () => {
    setCategoryToUpdate(null);
    setShowAddCategory(true);
  };

  const handleCategorySuccess = (action, message) => {
    if (action === "add") {
      toast.success("Category added successfully!");
    } else if (action === "update") {
      toast.success("Category updated successfully!");
    } else if (action === "error") {
      toast.error(message || "Operation failed");
    }
    setShowAddCategory(false);
    fetchData();
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Delete Category?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`newcategories/${id}`);
          toast.success("Category deleted successfully!");
          fetchData();
        } catch (error) {
          toast.error(error?.response?.data?.message || "Error deleting category!");
          console.error("Error deleting Catogory:", error);
        }
      }
    });
  };


  const handleFilterChange = (event) => {
    const selectedFilter = event.target.value;
    setFilter(selectedFilter);
    setCurrentPage(1);
  };

  // const getChartData = (data) => {
  //   const labels = data.map((item) => item.date);
  //   const prices = data.map((item) => parseFloat(item.price));
  //   return {
  //     labels,
  //     datasets: [
  //       {
  //         label: "Categoryes",
  //         data: prices,
  //         borderColor: "rgba(75, 192, 192, 1)",
  //         backgroundColor: "rgba(75, 192, 192, 0.2)",
  //         fill: true,
  //         tension: 0.4,
  //       },
  //     ],
  //   };
  // };



  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <ToastContainer position="top-right" />

      {/* ── Page Header ────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">📂 Categories</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Organize your menu — {pagination.total} categor{pagination.total !== 1 ? "ies" : "y"} total
          </p>
        </div>
        <div className="flex gap-3 flex-wrap items-center">

          <BulkCategoriesImport
            userId={userId}
            onImportComplete={(stats) => {
              fetchData();
            }}
            refreshCategories={fetchData}
          />

          <button
            onClick={handleAddCategory}
            className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-all shadow-sm shadow-teal-600/20"
          >
            <Plus size={16} /> Add Category
          </button>
        </div>
      </div>

      {/* ── Category Table ───────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <th className="px-4 py-3.5 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide">#</th>
                <th className="px-4 py-3.5 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide">Category</th>
                <th className="px-4 py-3.5 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide">Status</th>
                <th className="px-4 py-3.5 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide">Description</th>
                <th className="px-4 py-3.5 text-center font-semibold text-gray-600 text-xs uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td colSpan="5" className="py-16 text-center">
                    <div className="flex flex-col items-center gap-3 text-gray-400">
                      <span className="text-5xl">📂</span>
                      <p className="text-sm">No categories found.</p>
                      <button onClick={handleAddCategory}
                        className="text-sm text-teal-600 hover:text-teal-800 font-semibold underline underline-offset-2">
                        Add your first category →
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                data.map((item, index) => (
                  <tr key={item._id} className="border-b border-gray-100 hover:bg-teal-50/40 transition-colors">
                    <td className="px-4 py-3.5 text-gray-500 font-mono text-xs">
                      {(pagination.page - 1) * pagination.limit + index + 1}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        {item.image ? (
                          <img src={item.image} alt={item.newTitle} className="w-9 h-9 rounded-lg object-cover shrink-0 border border-gray-200" />
                        ) : (
                          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-100 to-teal-200 flex items-center justify-center shrink-0 text-teal-600 font-bold text-sm">
                            {(item.newTitle || "?")[0].toUpperCase()}
                          </div>
                        )}
                        <p className="font-semibold text-gray-800 text-sm">{item.newTitle || "—"}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${
                        item.newStatus === "Active" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                      }`}>
                        {item.newStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <p className="text-xs text-gray-500 truncate max-w-[200px]">{item.newDescription || "—"}</p>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleUpdateClick(item)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit Category"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(item._id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Category"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <Pagination
            totalPages={pagination.totalPages}
            currentPage={pagination.page}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </div>

      {/* AddCategory Modal */}
      {showAddCategory && (
        <AddOneCategory
          userId={userId}
          refreshCategories={fetchData}
          onSuccess={handleCategorySuccess}
          existingData={categoryToUpdate}
          isUpdateMode={!!categoryToUpdate}
          updateId={categoryToUpdate?._id}
          onClose={() => setShowAddCategory(false)}
        />
      )}
    </div>
  );
};

export default Categories;
